import { useEffect, useState } from "react";
import type {
  CopilotConversation,
  CopilotInstructionProfile,
  CopilotSavedPrompt,
  CopilotResponseLength,
  CopilotResponseStyle,
  CopilotSettings,
} from "@yazamco/shared-types";
import { Icon } from "../../components/ui/Icon";
import "./copilot-settings.css";

/** Which chats an edit applies to. The two are genuinely different objects: a
 *  conversation carries its own snapshot, taken when it was created. */
type Scope = "chat" | "defaults";

interface Props {
  settings: CopilotSettings;
  conversation: CopilotConversation | null;
  savedPrompts: CopilotSavedPrompt[];
  busy: boolean;
  onClose: () => void;
  onSaveDefaults: (input: Partial<CopilotSettings>) => void;
  onSaveConversation: (input: Partial<CopilotInstructionProfile>) => void;
  onCreateSavedPrompt: (input: { name: string; prompt: string }) => void;
  onDeleteSavedPrompt: (savedPrompt: CopilotSavedPrompt) => void;
}

const STYLES: Array<{ value: CopilotResponseStyle; label: string; hint: string }> = [
  { value: "precise", label: "Precise", hint: "Facts only" },
  { value: "balanced", label: "Balanced", hint: "Default" },
  { value: "creative", label: "Creative", hint: "Freer drafting" },
];

const LENGTHS: Array<{ value: CopilotResponseLength; label: string; hint: string }> = [
  { value: "brief", label: "Brief", hint: "A few lines" },
  { value: "normal", label: "Normal", hint: "Default" },
  { value: "detailed", label: "Detailed", hint: "Thorough" },
];

const BROWSER_ZONE = Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";
const TIME_ZONES = Array.from(
  new Set(["UTC", BROWSER_ZONE, ...Intl.supportedValuesOf("timeZone")]),
).sort((left, right) => left.localeCompare(right));

/**
 * The Copilot settings window, drawn as the canvas draws it: a 520px column
 * whose header carries the scope switch, a scrolling body of short labelled
 * sections, and a footer on a tinted ground.
 *
 * Two scopes, because they behave differently and hiding that would mislead:
 * "This chat" edits the snapshot the open conversation runs under and takes
 * effect on its next turn; "New chats" seeds future conversations and leaves
 * running ones exactly as they are.
 *
 * Every explanatory sentence the canvas shows as a "?" is a hint here rather
 * than body copy — that is what keeps the dialog one screen tall.
 */
export function CopilotSettingsDialog({
  settings,
  conversation,
  savedPrompts,
  busy,
  onClose,
  onSaveDefaults,
  onSaveConversation,
  onCreateSavedPrompt,
  onDeleteSavedPrompt,
}: Props): JSX.Element {
  const [scope, setScope] = useState<Scope>(conversation ? "chat" : "defaults");
  // The knowledge scope is deliberately not among these: it is chosen beside
  // the composer, where re-aiming the next question belongs, rather than in a
  // dialog about how answers are written.
  const source: Omit<CopilotInstructionProfile, "knowledgeScope"> =
    scope === "chat" && conversation ? conversation : settings;

  const [instructions, setInstructions] = useState(source.instructions);
  const [responseStyle, setResponseStyle] = useState(source.responseStyle);
  const [responseLength, setResponseLength] = useState(source.responseLength);
  const [responseLanguage, setResponseLanguage] = useState(source.responseLanguage);
  const [webGrounding, setWebGrounding] = useState(source.webGrounding);
  const [timeZone, setTimeZone] = useState(settings.timeZone ?? "");
  const [retainTranscripts, setRetainTranscripts] = useState(settings.retainTranscripts);
  const [mcpOnly, setMcpOnly] = useState(settings.mcpOnly);
  const [promptName, setSavedPromptName] = useState("");

  // Switching scope re-seeds the form from whichever object is now being
  // edited, so the fields always show what is actually in force there.
  useEffect(() => {
    setInstructions(source.instructions);
    setResponseStyle(source.responseStyle);
    setResponseLength(source.responseLength);
    setResponseLanguage(source.responseLanguage);
    setWebGrounding(source.webGrounding);
  }, [scope, source]);

  function save(): void {
    const profile = {
      instructions,
      responseStyle,
      responseLength,
      responseLanguage: responseLanguage.trim() || "auto",
      webGrounding,
    };
    if (scope === "chat" && conversation) {
      onSaveConversation(profile);
    } else {
      onSaveDefaults({
        ...profile,
        timeZone: timeZone.trim() || null,
        retainTranscripts,
        mcpOnly,
      });
    }
  }

  const isDefaults = scope === "defaults";

  return (
    <div
      className="dialog-overlay"
      role="dialog"
      aria-modal="true"
      aria-label="Copilot settings"
      onClick={onClose}
    >
      <div className="dialog cp-settings" onClick={(event) => event.stopPropagation()}>
        <div className="cp-settings-head">
          <h2>Copilot settings</h2>
          <button
            type="button"
            className="cp-dialog-close"
            onClick={onClose}
            aria-label="Close settings"
          >
            <Icon name="close" size={16} />
          </button>
        </div>

        <div className="cp-settings-scope">
          <div className="segmented" role="tablist" aria-label="Settings scope">
            <button
              type="button"
              role="tab"
              aria-selected={scope === "chat"}
              className="segmented-option"
              disabled={!conversation}
              title={conversation ? undefined : "Open a chat to change its own settings"}
              onClick={() => {
                setScope("chat");
              }}
            >
              This chat
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={scope === "defaults"}
              className="segmented-option"
              onClick={() => {
                setScope("defaults");
              }}
            >
              New chats
            </button>
            {/* A "Thinking models" tab stood here and edited them in place.
                Migration 0059 made the profile an agent, which also carries
                knowledge sources and webhook tools — more than a chat dialog
                should be asked to edit — so the one form for it lives in
                Copilot administration and this dialog only picks. */}
          </div>
        </div>

        {(
          <div className="cp-settings-body">
            <section>
              <div className="cp-settings-label">
                <label htmlFor="cp-instructions">Instructions</label>
                <InfoHint text="Guides how Copilot answers. It never widens what you can access." />
              </div>
              <textarea
                id="cp-instructions"
                rows={3}
                maxLength={4_000}
                value={instructions}
                placeholder="e.g. Answer briefly and always name the file or mail you used."
                onChange={(event) => setInstructions(event.target.value)}
              />

              <div className="cp-prompt-lib">
                {savedPrompts.map((savedPrompt) => (
                  <span key={savedPrompt.id} className="cp-prompt-chip">
                    <button
                      type="button"
                      onClick={() => setInstructions(savedPrompt.prompt)}
                      title={savedPrompt.prompt}
                    >
                      {savedPrompt.name}
                    </button>
                    <button
                      type="button"
                      onClick={() => onDeleteSavedPrompt(savedPrompt)}
                      aria-label={`Delete saved prompt ${savedPrompt.name}`}
                    >
                      <Icon name="close" size={10} stroke={2.4} />
                    </button>
                  </span>
                ))}
                <input
                  value={promptName}
                  maxLength={80}
                  placeholder="Save this prompt as…"
                  aria-label="Prompt name"
                  onChange={(event) => setSavedPromptName(event.target.value)}
                />
                <button
                  type="button"
                  className="cp-prompt-save"
                  disabled={!promptName.trim() || !instructions.trim() || busy}
                  onClick={() => {
                    onCreateSavedPrompt({
                      name: promptName.trim(),
                      prompt: instructions.trim(),
                    });
                    setSavedPromptName("");
                  }}
                >
                  Save prompt
                </button>
              </div>
            </section>

            <section>
              <span className="cp-settings-label" id="cp-style-label">
                Answer style
              </span>
              <div className="cp-choices" role="radiogroup" aria-labelledby="cp-style-label">
                {STYLES.map((option) => (
                  <button
                    key={option.value}
                    type="button"
                    role="radio"
                    aria-checked={responseStyle === option.value}
                    className="cp-choice"
                    title={option.hint}
                    onClick={() => setResponseStyle(option.value)}
                  >
                    <strong>{option.label}</strong>
                  </button>
                ))}
              </div>
            </section>

            <section>
              <span className="cp-settings-label" id="cp-length-label">
                Answer length
              </span>
              <div className="cp-choices" role="radiogroup" aria-labelledby="cp-length-label">
                {LENGTHS.map((option) => (
                  <button
                    key={option.value}
                    type="button"
                    role="radio"
                    aria-checked={responseLength === option.value}
                    className="cp-choice"
                    title={option.hint}
                    onClick={() => setResponseLength(option.value)}
                  >
                    <strong>{option.label}</strong>
                  </button>
                ))}
              </div>
            </section>

            <section className="cp-settings-grid">
              <div>
                <div className="cp-settings-label">
                  <label htmlFor="cp-language">Answer language</label>
                  <InfoHint text={'"auto" replies in the language you asked in.'} />
                </div>
                <input
                  id="cp-language"
                  list="cp-language-options"
                  maxLength={20}
                  value={responseLanguage}
                  onChange={(event) => setResponseLanguage(event.target.value)}
                />
                <datalist id="cp-language-options">
                  <option value="auto">auto</option>
                  <option value="Hebrew">Hebrew</option>
                  <option value="English">English</option>
                </datalist>
              </div>

              {isDefaults && (
                <div>
                  <div className="cp-settings-label">
                    <label htmlFor="cp-timezone">Time zone</label>
                    <InfoHint text='Used for "tomorrow" and "this week". Empty follows this browser.' />
                  </div>
                  <select
                    id="cp-timezone"
                    value={timeZone}
                    onChange={(event) => setTimeZone(event.target.value)}
                  >
                    <option value="">Browser default ({BROWSER_ZONE})</option>
                    {timeZone && !TIME_ZONES.includes(timeZone) && (
                      <option value={timeZone}>{timeZone}</option>
                    )}
                    {TIME_ZONES.map((zone) => (
                      <option key={zone} value={zone}>
                        {zone}
                      </option>
                    ))}
                  </select>
                </div>
              )}
            </section>

            <section className="cp-settings-switches">
              <label>
                <span>
                  <strong>Allow public web search</strong>
                  <InfoHint text="Off keeps answers to your work content only." />
                </span>
                <input
                  type="checkbox"
                  checked={webGrounding}
                  onChange={(event) => setWebGrounding(event.target.checked)}
                />
              </label>

              {isDefaults && (
                <label>
                  <span>
                    <strong>Use business tools as the only source</strong>
                    <InfoHint text="When business tools are active, return only their data. Microsoft 365, public web, agents and attached files are not searched." />
                  </span>
                  <input
                    type="checkbox"
                    checked={mcpOnly}
                    onChange={(event) => setMcpOnly(event.target.checked)}
                  />
                </label>
              )}

              {isDefaults && (
                <label>
                  <span>
                    <strong>Save chat history</strong>
                    <InfoHint text="Off keeps the chat list but stores no message text." />
                  </span>
                  <input
                    type="checkbox"
                    checked={retainTranscripts}
                    onChange={(event) => setRetainTranscripts(event.target.checked)}
                  />
                </label>
              )}
            </section>

            <p className="cp-settings-foot-note">
              <InfoHint text="Where Copilot searches is chosen beside the composer, not here — so you can re-aim the next question without leaving the chat." />
              Where Copilot searches is chosen beside the composer.
            </p>
          </div>
        )}

        <div className="dialog-actions">
          <button type="button" className="btn btn-secondary" onClick={onClose}>
            Cancel
          </button>
          {(
            <button type="button" className="btn btn-primary" disabled={busy} onClick={save}>
              {busy ? "Saving…" : "Save settings"}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

/**
 * The canvas's "?" — the sentence that explains a control, kept out of the
 * layout until it is wanted. `title` rather than a popover on purpose: it is
 * one sentence, it needs no interaction, and a popover would be a second thing
 * to dismiss inside a dialog.
 */
function InfoHint({ text }: { text: string }): JSX.Element {
  return (
    <span className="cp-info" title={text} role="img" aria-label={text}>
      <Icon name="info" size={14} />
    </span>
  );
}
